import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { CONVERTING_STATUS } from '@src/constants/project';
import { getBlockChanged } from '@src/services/project';
import { actionTypes } from './actions';

export const initialState = {
  projectId: '',
  multiBlock: {
    blocks: {},
    blockIds: [],
    originalBlockIds: [],
    originalBlocks: {},
    blockCredits: {},
  },
  title: '',
  createdAt: '',
  product: '',
  isPlaying: false,
  isPlayingBlock: false,
  currentPlayingBlockId: '',
  currentBlockId: '',
  audioLink: '',
  voiceDefault: {},
  listVoice: [],
  isDraft: false,
  isMultiBlockMode: false,
  duration: 0,
  currentTime: 0,
  isDownloading: false,
  isProjectSynthesizing: false,
  originalTitle: '',
  audioType: 'mp3',
  openDialogChangeFormat: false,
  originalAudioType: 'mp3',
};

export default function projectReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.SET_PROJECT_ID:
      return {
        ...state,
        projectId: action.payload,
      };

    case actionTypes.SET_MULTI_BLOCK:
      return {
        ...state,
        multiBlock: action.payload,
      };

    case actionTypes.SET_TITLE:
      return {
        ...state,
        title: action.payload,
      };

    case actionTypes.UPDATE_TITLE:
      return {
        ...state,
        title: action.payload,
        isDraft: true,
      };

    case actionTypes.SET_LIST_VOICE:
      return {
        ...state,
        listVoice: action.payload,
      };

    case actionTypes.SET_CREATED_AT:
      return {
        ...state,
        createdAt: action.payload,
      };

    case actionTypes.SET_PRODUCT:
      return {
        ...state,
        product: action.payload,
      };

    case actionTypes.SET_IS_PLAYING:
      return {
        ...state,
        isPlaying: action.payload,
      };

    case actionTypes.SET_IS_PLAYING_BLOCK:
      return {
        ...state,
        isPlayingBlock: action.payload,
      };

    case actionTypes.SET_CURRENT_BLOCK_ID:
      return {
        ...state,
        currentBlockId: action.payload,
      };

    case actionTypes.SET_CURRENT_PLAYING_BLOCK_ID:
      return {
        ...state,
        currentPlayingBlockId: action.payload,
      };

    case actionTypes.SET_VOICE_DEFAULT:
      return {
        ...state,
        voiceDefault: action.payload,
      };

    case actionTypes.SET_AUDIO_LINK:
      return {
        ...state,
        audioLink: action.payload,
      };

    case actionTypes.SET_IS_DRAFT:
      return {
        ...state,
        isDraft: action.payload,
      };

    case actionTypes.ADD_BLOCK: {
      const { blockId, newBlocks } = action.payload;

      const blocksWithIds = newBlocks.map((block) => ({
        ...block,
        id: block.id || uuidv4(),
        audioLink: null,
      }));

      const newBlockIds = [...state.multiBlock.blockIds];
      const index = newBlockIds.indexOf(blockId);
      if (index === -1) {
        newBlockIds.push(...blocksWithIds.map((b) => b.id));
      } else {
        newBlockIds.splice(index + 1, 0, ...blocksWithIds.map((b) => b.id));
      }

      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: newBlockIds,
          blocks: {
            ...state.multiBlock.blocks,
            ...blocksWithIds.reduce((acc, b) => {
              acc[b.id] = b;
              return acc;
            }, {}),
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK: {
      const { blockId, block } = action.payload;
      const shouldStopAudio =
        state.currentBlockId === blockId || state.isMultiBlockMode;

      return {
        ...state,
        isDraft: true,
        isPlaying: shouldStopAudio ? false : state.isPlaying,
        currentBlockId: shouldStopAudio ? '' : state.currentBlockId,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: {
              ...state.multiBlock.blocks[blockId],
              ...block,
              // Remove the success icon. This is too prevent when the icon is shown, and the setTimeout
              // is running but user edit the block, which will make the useEffect run the clean up function
              // which will delete the setTimeout.
              showSuccessIcon: false,
              isSample: false,
              isBlockEditting: true,
            },
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK_SYNTHESIS_INFO: {
      const { blockId, synthesisInfo } = action.payload;
      const { status } = synthesisInfo;
      const isSynthesisFailed = status === CONVERTING_STATUS.FAILURE;

      return {
        ...state,
        isDraft: isSynthesisFailed,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: {
              ...state.multiBlock.blocks[blockId],
              ...synthesisInfo,
              isBlockEditting: false,
            },
          },
        },
      };
    }

    case actionTypes.UPDATE_MULTI_BLOCK_SYNTHESIS_INFO: {
      const { blockIds, synthesisInfos } = action.payload;
      const isSynthesisFailed = synthesisInfos.some(
        (synthesisInfo) => synthesisInfo.status === CONVERTING_STATUS.FAILURE,
      );
      const isSynthesisInProgress = synthesisInfos.some(
        (synthesisInfo) =>
          synthesisInfo.status === CONVERTING_STATUS.IN_PROGRESS,
      );

      const newBlocks = blockIds.map((blockId) => ({
        ...state.multiBlock.blocks[blockId],
        ...synthesisInfos.find((synthesisInfo) => synthesisInfo.id === blockId),
        isBlockEditting: false,
      }));

      return {
        ...state,
        isDraft: isSynthesisFailed,
        isConverting: isSynthesisInProgress,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks.reduce((acc, block) => {
              acc[block.id] = block;
              return acc;
            }, {}),
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK_ELEMENTS: {
      const { blockId, elements } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: { ...state.multiBlock.blocks[blockId], elements },
          },
        },
      };
    }

    case actionTypes.UPDATE_ORDER_BLOCK: {
      const blockIds = action.payload;
      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds,
        },
      };
    }

    case actionTypes.DELETE_BLOCK: {
      const blockId = action.payload;
      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: state.multiBlock.blockIds.filter((id) => id !== blockId),
          blocks: omit(state.multiBlock.blocks, [blockId]),
        },
      };
    }

    case actionTypes.UPDATE_DURATION: {
      return {
        ...state,
        duration: action.payload,
      };
    }

    case actionTypes.UPDATE_CURRENT_TIME: {
      return {
        ...state,
        currentTime: action.payload,
      };
    }

    case actionTypes.SET_IS_DOWNLOADING: {
      return {
        ...state,
        isDownloading: action.payload,
      };
    }

    case actionTypes.SET_IS_MULTI_BLOCK_MODE:
      return {
        ...state,
        isMultiBlockMode: action.payload,
      };

    case actionTypes.UPDATE_BLOCKS_WITH_PRONUNCIATION_CHANGE: {
      const { pronunciationChange } = action;
      const newBlocks = Object.values(state.multiBlock.blocks)
        .filter((block) =>
          block.text.toLowerCase().includes(pronunciationChange.toLowerCase()),
        )
        .map((block) => ({
          ...block,
          audioLink: null,
          status: null,
        }))
        .reduce((acc, block) => {
          acc[block.id] = block;
          return acc;
        }, {});

      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks,
          },
        },
      };
    }

    case actionTypes.UPDATE_ORIGINAL_BLOCKS: {
      const blockId = action.payload;
      const { audioType } = state;
      let originalBlocks = { ...state.multiBlock.originalBlocks };

      if (blockId) {
        originalBlocks[blockId] = state.multiBlock.blocks[blockId];
      } else {
        originalBlocks = state?.multiBlock?.blocks;
      }
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          originalBlocks,
          originalBlockIds: state?.multiBlock?.blockIds,
        },
        originalTitle: state.title,
        originalAudioType: audioType,
      };
    }

    case actionTypes.SET_IS_PROJECT_SYNTHESIZING: {
      return {
        ...state,
        isProjectSynthesizing: action.payload,
      };
    }
    case actionTypes.UPDATE_CREDITS_BLOCKS: {
      const { blockId, credits } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blockCredits: {
            ...state.multiBlock.blockCredits,
            [blockId]: credits,
          },
        },
      };
    }

    case actionTypes.DELETE_CREDITS_BLOCKS: {
      const { blockId } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blockCredits: omit(state.multiBlock.blockCredits, [blockId]),
        },
      };
    }

    case actionTypes.SET_ORIGINAL_TITLE: {
      return {
        ...state,
        originalTitle: action.payload,
      };
    }

    case actionTypes.SET_AUDIO_TYPE: {
      return {
        ...state,
        audioType: action.payload,
      };
    }

    case actionTypes.SET_OPEN_DIALOG_CHANGE_FORMAT: {
      return {
        ...state,
        openDialogChangeFormat: action.payload,
      };
    }

    case actionTypes.SAVE_PROJECT: {
      const { blocks, originalBlocks, blockIds } = state.multiBlock;
      const blockWithText = Object.keys(blocks)
        .filter((blockId) => blocks[blockId].text)
        .map((blockId) => blocks[blockId]);
      const blockChanged = getBlockChanged(blockWithText, originalBlocks);
      const newBlocks = blockChanged?.reduce((acc, block) => {
        acc[block.id] = {
          ...block,
          audioLink: null,
        };
        return acc;
      }, {});
      return {
        ...state,
        originalTitle: state.title,
        originalAudioType: state.audioType,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...blocks,
            ...newBlocks,
          },
          originalBlocks: {
            ...blocks,
            ...newBlocks,
          },
          originalBlockIds: blockIds,
        },
      };
    }

    case actionTypes.SET_ORIGINAL_AUDIO_TYPE: {
      return {
        ...state,
        originalAudioType: action.payload,
      };
    }
    default:
      return state;
  }
}
